import {
  createSelector,
  MemoizedSelector
} from '@ngrx/store';
import { selectDigitaixState } from '../core.selectors';
import { getAllDocuments } from '../documents/document.selectors';
import * as _merge from 'deepmerge';
import { getRowId } from './utilities';
import {
  DxDocument,
  RequiredDocumentMeta,
  ActionButtonDefinition
} from '@digitaix/types';

const deepmerge = _merge;

export const selectTablesState = createSelector(
  selectDigitaixState,
  state => state.tables
);

export const selectTableState = (tableId: string) =>
  createSelector(
    selectTablesState,
    state => state[tableId] || null
  );

export const getTableSorting = (tableId: string) =>
  createSelector(
    selectTableState(tableId),
    state => state && state.sorting
  );

export const getTablePagination = (tableId: string) =>
  createSelector(
    selectTableState(tableId),
    state => state && state.pagination
  );

export const getTableFiltering = (tableId: string) =>
  createSelector(
    selectTableState(tableId),
    state => state && state.filtering
  );

export const isSotringEnabled = (tableId: string) =>
  createSelector(
    getTableSorting(tableId),
    sorting => sorting && sorting.__enabled
  );

export const isPaginationEnabled = (tableId: string) =>
  createSelector(
    getTablePagination(tableId),
    pagination => pagination && pagination.enabled
  );

export const isFilteringEnabled = (tableId: string) =>
  createSelector(
    getTableFiltering(tableId),
    filtering => filtering && filtering.__enabled
  );

export const getTableLimit = (tableId: string) =>
  createSelector(
    selectTableState(tableId),
    state => state && state.limit
  );

export const getColumnFilter = (tableId: string, column: string) =>
  createSelector(
    getTableFiltering(tableId),
    filtering =>
      (filtering && filtering[column]) || { enabled: true, value: null }
  );

export const isColumnFilterEnabled = (tableId: string, column: string) =>
  createSelector(
    getColumnFilter(tableId, column),
    filter => filter.enabled
  );

export const getColumnFilterValue = (tableId: string, column: string) =>
  createSelector(
    getColumnFilter(tableId, column),
    filter => filter.value
  );

export const getColumnSorting = (tableId: string, column: string) =>
  createSelector(
    getTableSorting(tableId),
    sorting => (sorting && sorting[column]) || { enabled: true, direction: 0 }
  );

export const isColumnSortingEnabled = (tableId: string, column: string) =>
  createSelector(
    getColumnSorting(tableId, column),
    sorting => sorting.enabled
  );

export const getColumnSortingDirection = (tableId: string, column: string) =>
  createSelector(
    getColumnSorting(tableId, column),
    sorting => sorting.direction
  );

export const selectExpandedTableState = (tableId: string) =>
  createSelector(
    selectTableState(tableId),
    state => state && state.expandable
  );

export const getExpandedTableRow = (tableId: string) =>
  createSelector(
    selectExpandedTableState(tableId),
    state => state && state.row
  );

export const getExpandedTableRowId = (tableId: string) =>
  createSelector(
    getExpandedTableRow(tableId),
    row => getRowId(row)
  );

export const isExpandedTableRowEditMode = (tableId: string) =>
  createSelector(
    selectExpandedTableState(tableId),
    state => state && state.edit
  );

export const isExpandedTableRowFullMode = (tableId: string) =>
  createSelector(
    selectExpandedTableState(tableId),
    state => state && state.full
  );

export const isImportTableDataEnabled = (tableId: string) =>
  createSelector(
    selectTableState(tableId),
    state => state && state.importTableData
  );

export const isExportTableDataEnabled = (tableId: string) =>
  createSelector(
    selectTableState(tableId),
    state => state && state.exportTableData
  );

export const getTableColumns = (tableId: string) =>
  createSelector(
    selectTableState(tableId),
    state => (state && state.columns) || []
  );

export const getTableDisplayedColumns = (tableId: string) =>
  createSelector(
    selectTableState(tableId),
    state => {
      if (state) {
        const displayedColumns = [];
        if (state.selectableRows) {
          displayedColumns.push('__checkbox');
        }
        if (state.expandRow) {
          displayedColumns.push('expandControls');
        }
        if (state.sortable) {
          displayedColumns.push('__sortable');
        }
        if (state.showCreatedAt) {
          displayedColumns.push('__createdAt');
        }
        if (state.showUpdatedAt) {
          displayedColumns.push('__updatedAt');
        }
        if (state.displayedColumns.length) {
          displayedColumns.push(...state.displayedColumns);
        } else {
          displayedColumns.push(...state.columns.map(column => column.name));
        }
        displayedColumns.push('controls');

        const hiddenColumns = [...state.hiddenColumns];

        if (
          state &&
          state.currentTab !== null &&
          state.tabs[state.currentTab] &&
          state.tabs[state.currentTab].tableState &&
          state.tabs[state.currentTab].tableState.hiddenColumns &&
          state.tabs[state.currentTab].tableState.hiddenColumns.length
        ) {
          hiddenColumns.push(
            ...state.tabs[state.currentTab].tableState.hiddenColumns
          );
        }

        return displayedColumns.filter(
          column => !hiddenColumns.includes(column)
        );
      }
      return [];
    }
  );

export const getTableFormId = (tableId: string) =>
  createSelector(
    selectTableState(tableId),
    state => state && state.formId
  );

export const getTableEditFormId = (tableId: string) =>
  createSelector(
    selectTableState(tableId),
    state => state && state.editFormId
  );

export const getTableCreateFormId = (tableId: string) =>
  createSelector(
    selectTableState(tableId),
    state => state && state.createFormId
  );

export const hasTableFormId = (tableId: string) =>
  createSelector(
    selectTableState(tableId),
    state => !!(state && state.formId)
  );

export const hasTableEditFormId = (tableId: string) =>
  createSelector(
    selectTableState(tableId),
    state => !!(state && state.editFormId)
  );

export const hasTableCreateFormId = (tableId: string) =>
  createSelector(
    selectTableState(tableId),
    state => !!(state && state.createFormId)
  );

export const isEditRowEnabled = (tableId: string) =>
  createSelector(
    selectTableState(tableId),
    state => state && state.editRow
  );

export const isDownloadRowEnabled = (tableId: string) =>
  createSelector(
    selectTableState(tableId),
    state => state && state.downloadRow
  );

export const getViewComponent = (tableId: string) =>
  createSelector(
    selectTableState(tableId),
    state => state && state.viewComponent
  );

export const hasTableViewComponent = (tableId: string) =>
  createSelector(
    getViewComponent(tableId),
    viewComponent => !!viewComponent
  );

export const isExpandRowEnabled = (tableId: string) =>
  createSelector(
    selectTableState(tableId),
    hasTableViewComponent(tableId),
    hasTableEditFormId(tableId),
    (state, hasViewComponent, hasFormId) =>
      (hasViewComponent || hasFormId) && state && state.expandRow
  );

export const isDeleteRowEnabled = (tableId: string) =>
  createSelector(
    selectTableState(tableId),
    state => state && state.deleteRow
  );

export const isAddRowEnabled = (tableId: string) =>
  createSelector(
    selectTableState(tableId),
    hasTableCreateFormId(tableId),
    (state, hasFormId) => hasFormId && state && state.addRow
  );

export const isArchiveRowEnabled = (tableId: string) =>
  createSelector(
    selectTableState(tableId),
    state => state && state.archiveRow
  );

export const isRestoreRowEnabled = (tableId: string) =>
  createSelector(
    selectTableState(tableId),
    state => state && state.restoreRow
  );

export const isViewRowEnabled = (tableId: string) =>
  createSelector(
    selectTableState(tableId),
    hasTableViewComponent(tableId),
    (state, hasViewComponent) => hasViewComponent && state && state.viewRow
  );

export const isShowArchivedEnabled = (tableId: string) =>
  createSelector(
    selectTableState(tableId),
    state => state && state.showArchived
  );

export const isShowCreatedAtEnabled = (tableId: string) =>
  createSelector(
    selectTableState(tableId),
    state => state && state.showCreatedAt
  );

export const isShowUpdateAtEnabled = (tableId: string) =>
  createSelector(
    selectTableState(tableId),
    state => state && state.showUpdatedAt
  );

export const isDialogViewMode = (tableId: string) =>
  createSelector(
    selectTableState(tableId),
    hasTableViewComponent(tableId),
    (state, hasViewComponent) => hasViewComponent && state && state.dialogView
  );

export const isDialogEditMode = (tableId: string) =>
  createSelector(
    selectTableState(tableId),
    hasTableEditFormId(tableId),
    (state, hasFormId) => hasFormId && state && state.dialogEdit
  );

export const isExpandViewMode = (tableId: string) =>
  createSelector(
    selectTableState(tableId),
    hasTableViewComponent(tableId),
    (state, hasViewComponent) => hasViewComponent && state && state.expandView
  );

export const isExpandEditMode = (tableId: string) =>
  createSelector(
    selectTableState(tableId),
    hasTableEditFormId(tableId),
    (state, hasFormId) => hasFormId && state && state.expandEdit
  );

export const getTableTitle = (tableId: string) =>
  createSelector(
    selectTableState(tableId),
    state => state && state.title
  );

export const getTableDeleteMessage = (tableId: string) =>
  createSelector(
    selectTableState(tableId),
    state => state.deleteMessage
  );

export const getTableSubtitle = (tableId: string) =>
  createSelector(
    selectTableState(tableId),
    state => state && state.subtitle
  );

export const getTableCollationPath = (tableId: string) =>
  createSelector(
    selectTableState(tableId),
    state => state && state.collectionPath
  );

export const getTableData: <T extends DxDocument>(
  tableId: string,
  collectionPath: string
) => MemoizedSelector<any, Array<RequiredDocumentMeta<T>>> = <T>(
  tableId: string,
  collectionPath: string
) => getAllDocuments<T>(collectionPath, true);

export const isTableRowExpanded = (tableId: string) =>
  createSelector(
    getExpandedTableRowId(tableId),
    rowId => !!rowId
  );

export const getPageSizeOptions = (tableId: string) =>
  createSelector(
    getTablePagination(tableId),
    pagination => (pagination && pagination.pageSizeOptions) || []
  );

export const getTableSettingsState = (tableId: string) =>
  createSelector(
    selectTableState(tableId),
    state => state && state.tableSettings
  );

export const isTableSettingsEnabled = (tableId: string) =>
  createSelector(
    getTableSettingsState(tableId),
    tableSettings => tableSettings && tableSettings.enabled
  );

export const isTableSettingArchivedEnabled = (tableId: string) =>
  createSelector(
    getTableSettingsState(tableId),
    tableSettings =>
      tableSettings && tableSettings.enabled && tableSettings.archived
  );

export const isTableSettingUpdatedAtEnabled = (tableId: string) =>
  createSelector(
    getTableSettingsState(tableId),
    tableSettings =>
      tableSettings && tableSettings.enabled && tableSettings.updatedAt
  );

export const isTableSettingCreatedAtEnabled = (tableId: string) =>
  createSelector(
    getTableSettingsState(tableId),
    tableSettings =>
      tableSettings && tableSettings.enabled && tableSettings.createdAt
  );

export const isTableSettingDialogEditEnabled = (tableId: string) =>
  createSelector(
    getTableSettingsState(tableId),
    tableSettings =>
      tableSettings && tableSettings.enabled && tableSettings.dialogEdit
  );

export const isTableSettingDialogViewEnabled = (tableId: string) =>
  createSelector(
    getTableSettingsState(tableId),
    tableSettings =>
      tableSettings && tableSettings.enabled && tableSettings.dialogView
  );

export const getHiddenColumns = (tableId: string) =>
  createSelector(
    selectTableState(tableId),
    tableState => (tableState && tableState.hiddenColumns) || []
  );

export const getColumsWithHiddenInfo: (tableId: string) => MemoizedSelector<any,any> = (tableId: string) =>
  createSelector(
    getTableColumns(tableId),
    getHiddenColumns(tableId),
    (columns, hiddenColumns) =>
      columns.map(column => ({
        ...column,
        hidden: hiddenColumns.includes(column.name)
      }))
  );

export const getDialogTitle = (tableId: string) =>
  createSelector(
    selectTableState(tableId),
    tableState => tableState && tableState.getDialogTitle
  );

export const getTableTabs = (tableId: string) =>
  createSelector(
    selectTableState(tableId),
    state => (state && state.tabs) || []
  );

export const isWithCategories = (tableId: string) =>
  createSelector(
    selectTableState(tableId),
    state => !!(state && state.categoriesCollectionPath)
  );

export const getCategoriesCollectionPath = (tableId: string) =>
  createSelector(
    selectTableState(tableId),
    state => (state && state.categoriesCollectionPath) || null
  );

export const getSelectedRowsObject = (tableId: string) =>
  createSelector(
    selectTableState(tableId),
    state => (state && state.selectedRows) || {} || {}
  );

export const getSelectedRows = (tableId: string) =>
  createSelector(
    getSelectedRowsObject(tableId),
    selectedRows =>
      Object.entries(selectedRows).map(([key, value]) => ({
        ...value,
        __index: +key
      })) || []
  );

export const getTableFilter: (tableId: string) => MemoizedSelector<any, any> = (tableId: string) =>
  createSelector(
    selectTableState(tableId),
    state =>
      Object.entries((state && state.filtering) || {})
        .filter(([key, value]) => typeof value === 'object')
        .filter(
          ([key, value]: [string, { enabled: boolean; value: any }]) =>
            value.enabled !== false && !!value.value
        )
        .map(
          ([key, value]: [
            string,
            { enabled: boolean; value: string | number | boolean }
          ]) => ({ column: key, filter: value.value })
        )
  );

export const showExpandableBtn = (tableId: string) =>
  createSelector(
    selectTableState(tableId),
    state => !!(state && state.expandable && state.expandable.btn)
  );

export const isManuelDelete = (tableId: string) =>
  createSelector(
    selectTableState(tableId),
    state => !!(state && state.manuelDelete)
  );

export const isManuelArchive = (tableId: string) =>
  createSelector(
    selectTableState(tableId),
    state => !!(state && state.manuelArchive)
  );

export const getTableActions: (
  tableId: string
) => MemoizedSelector<any, ActionButtonDefinition<any>[]> = (tableId: string) =>
  createSelector(
    selectTableState(tableId),
    state => (state && state.actions) || []
  );

export const getTableRowStyle = (tableId: string) =>
  createSelector(
    selectTableState(tableId),
    state => (state && state.rowStyles) || null
  );

export const getTableRowClasses = (tableId: string) =>
  createSelector(
    selectTableState(tableId),
    state => (state && state.rowClasses) || null
  );

export const getTableViewComponentId = (tableId: string) =>
  createSelector(
    selectTableState(tableId),
    state => (state && state.viewComponentId) || null
  );
